/**
 * Escrow encryption: PBKDF2-SHA256 (600000 iterations, random 16-byte salt)
 * deriving an AES-256-GCM key; random 12-byte IV.
 *
 * Envelope format: ALL-JSON, meaning { v, kdf, iterations, salt_b64, iv_b64,
 * alg, ct_b64 }. Tradeoff: base64 inflates the ciphertext ~33% versus a
 * header-line + raw-bytes binary layout, but keeps the archive a single
 * self-describing JSON document that trivially round-trips through text
 * tooling, and the dominant payload (audio Blobs) is already base64 inside
 * the plaintext manifest, so the relative overhead is bounded.
 *
 * scripts/decrypt-content-escrow.mjs re-implements this decrypt EXACTLY
 * (node:crypto webcrypto); change the two in lockstep.
 */

import { EscrowError, type EscrowEnvelope } from './types';
import { base64ToBytes, bytesToBase64, getSubtle } from './canonical';

export const KDF_ITERATIONS = 600_000;
export const MIN_PASSPHRASE_CODE_POINTS = 20;
export const MIN_PASSPHRASE_UNIQUE_CODE_POINTS = 8;

/**
 * Structural floor for newly-created archives. It is intentionally applied
 * only on encryption: the decrypt path must remain able to recover an older
 * archive whose passphrase would not satisfy today's creation policy.
 *
 * This is not an entropy estimator. Operators still generate and retain one
 * random passphrase per context in the password manager. The floor catches
 * the dangerous mistakes this API can identify locally: blank/short input,
 * low-variety filler, and a short repeated pattern.
 */
export function assertStrongPassphrase(passphrase: string): void {
  if (typeof passphrase !== 'string') throw new EscrowError('weak-passphrase');
  const chars = Array.from(passphrase.normalize('NFKC'));
  if (
    chars.length < MIN_PASSPHRASE_CODE_POINTS ||
    passphrase.trim().length === 0 ||
    new Set(chars).size < MIN_PASSPHRASE_UNIQUE_CODE_POINTS
  ) {
    throw new EscrowError('weak-passphrase');
  }
  const compact = chars.join('').toLocaleLowerCase('en-US').replace(/\s+/g, '');
  if (/^(.{1,12})\1+$/.test(compact)) throw new EscrowError('weak-passphrase');
}

async function deriveKey(
  subtle: SubtleCrypto,
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const material = await subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptJson(obj: unknown, passphrase: string): Promise<EscrowEnvelope> {
  assertStrongPassphrase(passphrase);
  const subtle = await getSubtle();
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (!cryptoObj) throw new Error('no-crypto');
  const salt = cryptoObj.getRandomValues(new Uint8Array(16));
  const iv = cryptoObj.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(subtle, passphrase, salt, KDF_ITERATIONS);
  const plaintext = new TextEncoder().encode(JSON.stringify(obj));
  const ct = new Uint8Array(
    await subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource),
  );
  return {
    v: 1,
    kdf: 'PBKDF2-SHA256',
    iterations: KDF_ITERATIONS,
    salt_b64: bytesToBase64(salt),
    iv_b64: bytesToBase64(iv),
    alg: 'A256GCM',
    ct_b64: bytesToBase64(ct),
  };
}

export async function decryptJson(envelope: EscrowEnvelope, passphrase: string): Promise<unknown> {
  const envelopeKeys = envelope && typeof envelope === 'object' && !Array.isArray(envelope)
    ? Object.keys(envelope).sort()
    : [];
  const expectedEnvelopeKeys = [
    'alg', 'ct_b64', 'iterations', 'iv_b64', 'kdf', 'salt_b64', 'v',
  ];
  if (
    !envelope ||
    JSON.stringify(envelopeKeys) !== JSON.stringify(expectedEnvelopeKeys) ||
    envelope.v !== 1 ||
    envelope.kdf !== 'PBKDF2-SHA256' ||
    envelope.alg !== 'A256GCM' ||
    typeof envelope.ct_b64 !== 'string' ||
    typeof envelope.salt_b64 !== 'string' ||
    typeof envelope.iv_b64 !== 'string' ||
    !Number.isSafeInteger(envelope.iterations) ||
    envelope.iterations < 1
  ) {
    throw new EscrowError('bad-envelope');
  }
  const subtle = await getSubtle();
  let plaintext: Uint8Array;
  try {
    const key = await deriveKey(
      subtle,
      passphrase,
      base64ToBytes(envelope.salt_b64),
      envelope.iterations,
    );
    const ct = base64ToBytes(envelope.ct_b64);
    plaintext = new Uint8Array(
      await subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToBytes(envelope.iv_b64) as BufferSource },
        key,
        ct as BufferSource,
      ),
    );
  } catch {
    // Wrong passphrase and tampered ciphertext are deliberately
    // indistinguishable (GCM auth failure).
    throw new EscrowError('decrypt-failed');
  }
  try {
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    throw new EscrowError('decrypt-failed');
  }
}
